import React, { memo, useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { RequestStatus } from '@atlassian/navigation-bff-data-provider';

import { ANALYTICS_CHANNEL } from '../../constants';
import type { GlobalShortcutComponentItemBase, MappedGlobalShortcutsItem } from '../../types';
import { getUrlMaybe } from '../../utils/get-url-maybe';
import { MenuComponentItem } from '../menu-component-item';
import { fireMenuLinkItemClickAnalyticsEvent, MenuLinkItem } from '../menu-link-item';

import { messages } from './messages';

export type ShortcutMenuItemProps = {
	// This component is exported from the package, which means we can't rely on the context provider that Global Shortcuts uses to determine if Drag & Drop is enabled, as consumers might be using something else.
	// Thus, this value needs to be provided as a prop by whatever is rendering it.
	dragAndDropEnabled?: boolean;
	isInMoreMenu?: boolean;
	openInSameTab?: boolean;
	product?: string;
	shortcut: MappedGlobalShortcutsItem;
	status?: RequestStatus | null;
};

/** Unmemoized implementation; exported for tests that assert render counts. */
export function ShortcutMenuItemInner({
	shortcut,
	dragAndDropEnabled,
	status,
	product,
	openInSameTab,
	isInMoreMenu,
}: ShortcutMenuItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsName = shortcut.analyticsName || `unknown_${shortcut.label}`;
	const ItemWrapper = shortcut?.ItemWrapper ?? React.Fragment;

	/**
	 * Passed to MenuLinkItem when the inner item disables itself; uses BFF `status` for analytics context.
	 */
	const onDisable = useCallback(
		(reason?: string) => {
			// Do not fire analytics event until loading is complete
			if (!status || status === RequestStatus.INIT || status === RequestStatus.LOADING) {
				return;
			}

			// This currently covers reasons like request errors.
			// If the BFF gives us more information in the future (such as permissions), that should be accounted for here too.
			const bffReason = status !== RequestStatus.COMPLETE ? status : undefined;

			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'disabled',
				actionSubject: 'button',
				actionSubjectId: 'globalNavigation',
				attributes: {
					name: analyticsName,
					reason: reason ?? bffReason,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, analyticsName, status],
	);

	const customComponentDraggableOnClick = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [analyticsName, createAnalyticsEvent]);

	const customComponentOnClick = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
		shortcut.onClick?.();
	}, [analyticsName, createAnalyticsEvent, shortcut]);

	if (shortcut.type === 'component') {
		if (fg('navx-3539-custom-shortcut-component-draggable')) {
			return (
				<MenuComponentItem
					shortcut={shortcut}
					dragAndDropEnabled={dragAndDropEnabled}
					isInMoreMenu={isInMoreMenu}
					onClick={customComponentDraggableOnClick}
				/>
			);
		}

		const Component = shortcut.component as GlobalShortcutComponentItemBase['component'];
		return (
			<ItemWrapper>
				<Component onClick={customComponentOnClick} isInMoreMenu={isInMoreMenu} />
			</ItemWrapper>
		);
	}

	if (shortcut.type === 'internal') {
		return (
			<ItemWrapper>
				<MenuLinkItem
					analyticsName={analyticsName}
					key={shortcut.key}
					icon={shortcut.icon}
					url={getUrlMaybe(shortcut)}
					label={shortcut.label}
					labelElem={shortcut.labelElem}
					onDisable={onDisable}
					isReorderable={shortcut.isReorderable}
					openInSameTab={true}
					onClick={shortcut.onClick}
					onRightClick={shortcut.onRightClick}
					product={product}
					isSelected={shortcut.isSelected}
				/>
			</ItemWrapper>
		);
	}

	return (
		<ItemWrapper>
			<MenuLinkItem
				optionalRef={shortcut.ref}
				analyticsName={analyticsName}
				key={shortcut.key}
				apsKey={shortcut.apsKey}
				icon={shortcut.icon}
				url={getUrlMaybe(shortcut)}
				label={shortcut.label}
				labelElem={shortcut.labelElem}
				onDisable={onDisable}
				actions={shortcut.actions}
				ariaLabel={formatMessage(messages.menuLinkItemAriaLabel, {
					appName: shortcut.label,
					siteName: '',
				})}
				onClick={shortcut.onClick}
				isReorderable={shortcut.isReorderable}
				onRightClick={shortcut.onRightClick}
				onMouseEnter={shortcut.onMouseEnter}
				onFocus={shortcut.onFocus}
				menuItemId={shortcut.menuItemId}
				product={product}
				openInSameTab={openInSameTab}
				optionsMenuElem={shortcut.optionsMenuElem}
			/>
		</ItemWrapper>
	);
}

export const ShortcutMenuItem = memo(ShortcutMenuItemInner);
